<template>
  <v-dialog 
    :model-value="modelValue" 
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="600px"
  >
    <v-card>
      <v-card-title>
        <span class="headline">Create New Case</span>
      </v-card-title>

      <v-card-text>
        <v-form>
          <v-text-field 
            label="Case Name" 
            v-model="newCaseName" 
            required
          ></v-text-field>
          <v-select 
            label="Anatomy" 
            :items="anatomies" 
            v-model="selectedAnatomy" 
            item-title="text" 
            item-value="value"
            required
          ></v-select>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="hasDicomLoaded"
          color="primary"
          :loading="submitting"
          @click="handleCreate(true)"
        >
          <v-icon left>mdi-cloud-upload</v-icon>
          Create & Submit
        </v-btn>
        <v-btn v-if="!hasDicomLoaded" :disabled="submitting" @click="handleCreate(false)">
          <v-icon left>mdi-plus-box</v-icon>
          Create Case
        </v-btn>
        <v-btn :disabled="submitting" @click="$emit('update:modelValue', false)">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useGirderStore } from '@/src/store/girder-store';
import { useDatasetStore } from '@/src/store/datasets';
import { useDICOMStore, ANONYMOUS_PATIENT_ID } from '@/src/store/datasets-dicom';
import { useMessageStore } from '@/src/store/messages';
import { progressCallback } from '@/src/store/progress-store';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['update:modelValue', 'created']);

const girder = useGirderStore();
const datasetStore = useDatasetStore();
const dicomStore = useDICOMStore();
const DEFAULT_TASK = 'Reconstruct v2 alpha';
const DEFAULT_ANATOMY = 'Cranium';

const newCaseName = ref('');
const selectedAnatomy = ref(DEFAULT_ANATOMY);
const selectedTask = ref(DEFAULT_TASK);
const submitting = ref(false);

// Patient ID of the currently loaded DICOM, or '' if none/anonymous/non-DICOM.
function loadedPatientId() {
  const sel = datasetStore.primarySelection;
  if (sel?.type !== 'dicom') return '';
  const studyKey = dicomStore.volumeStudy[sel.volumeKey];
  const patientKey = studyKey ? dicomStore.studyPatient[studyKey] : undefined;
  const patientId = patientKey ? dicomStore.patientInfo[patientKey]?.PatientID : undefined;
  return !patientId || patientId === ANONYMOUS_PATIENT_ID ? '' : patientId;
}

// Prefill the form each time the dialog opens.
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      newCaseName.value = loadedPatientId();
      selectedAnatomy.value = DEFAULT_ANATOMY;
      selectedTask.value = DEFAULT_TASK;
    }
  }
);

const anatomies = computed(() => girder.anatomies);

// True when a DICOM/image volume is already loaded in the scene, so the case
// can be created and submitted for processing in a single step.
const hasDicomLoaded = computed(() => {
  const sel = datasetStore.primarySelection;
  return sel?.type === 'image' || sel?.type === 'dicom';
});

async function handleCreate(submit = false) {
  if (!newCaseName.value || !selectedAnatomy.value || !selectedTask.value) {
    alert('Please fill all required fields.');
    return;
  }

  const messageStore = useMessageStore();
  submitting.value = true;
  try {
    const caseData = await girder.createCase({
      name: newCaseName.value,
      anatomy: selectedAnatomy.value,
      anatomicalSide: 'Left',
      task: selectedTask.value,
    });

    girder.setLoadedCase(caseData);

    // When a DICOM is already loaded, upload it and submit the job in the same
    // step so the user doesn't have to press "Upload & Submit" afterwards.
    if (submit) {
      try {
        await girder.uploadData(caseData._id, selectedTask.value, progressCallback);
        messageStore.addSuccess('Case created and submitted for processing');
      } catch (error) {
        messageStore.addError('Case created, but upload failed: ' + error.message);
      }
    }

    emit('created', caseData, submit);
    emit('update:modelValue', false);

    // Reset form
    newCaseName.value = '';
    selectedAnatomy.value = DEFAULT_ANATOMY;
    selectedTask.value = DEFAULT_TASK;
  } catch (error) {
    console.error('Failed to create case:', error);
  } finally {
    submitting.value = false;
  }
}
</script> 